// ════════════════════════════════════════════════════════════
// DialogoIncidencia — formulario para pedir ayuda al SIS
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box,
  ToggleButton, ToggleButtonGroup, Alert, Typography
} from '@mui/material';
import { crearIncidencia } from '../api';

const TIPOS = [
  { id: 'error',    label: '🐞 Algo no funciona' },
  { id: 'duda',     label: '❓ Tengo una duda' },
  { id: 'mejora',   label: '💡 Sugerencia' },
  { id: 'peticion', label: '📋 Petición' },
];

export default function DialogoIncidencia({ open, onClose }: any) {
  const [tipo, setTipo] = useState('error');
  const [descripcion, setDescripcion] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [enviada, setEnviada] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setTipo('error');
    setDescripcion('');
    setArchivo(null);
    setEnviada(null);
  }, [open]);

  const enviar = async () => {
    if (descripcion.trim().length < 10) {
      alert('Cuéntanos un poco más para poder ayudarte (al menos 10 caracteres).');
      return;
    }
    setEnviando(true);
    try {
      const r = await crearIncidencia({
        tipo, descripcion: descripcion.trim(),
        pantalla: window.location.pathname,
        navegador: navigator.userAgent,
      }, archivo);
      setEnviada(r.id);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'No se pudo enviar. Inténtalo de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={!!open} maxWidth="sm" fullWidth
      onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') onClose(); }}>
      <DialogTitle>🆘 Solicitar ayuda</DialogTitle>
      <DialogContent>
        {enviada ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            <Typography sx={{ fontWeight: 700 }}>Recibido (incidencia #{enviada})</Typography>
            <Typography variant="body2">
              Se ha avisado al equipo del SIS. Te responderemos por correo en cuanto esté resuelto.
            </Typography>
          </Alert>
        ) : (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Cuéntanos qué te ocurre. Se enviará junto con la pantalla en la que estás,
              para que podamos localizarlo antes.
            </Typography>

            <ToggleButtonGroup exclusive value={tipo} size="small" sx={{ mb: 2, flexWrap: 'wrap' }}
              onChange={(_, v) => { if (v) setTipo(v); }}>
              {TIPOS.map(t => (
                <ToggleButton key={t.id} value={t.id} sx={{ textTransform: 'none' }}>
                  {t.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <TextField fullWidth multiline rows={5} autoFocus
              label="¿Qué ha pasado?"
              placeholder={tipo === 'error'
                ? 'Ej: al guardar una intervención sale un error y no se queda grabada.'
                : 'Escribe aquí tu consulta...'}
              value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />

            <Button variant="outlined" component="label" fullWidth sx={{ mt: 2 }}>
              {archivo ? `📎 ${archivo.name}` : '📷 Adjuntar captura o foto (opcional)'}
              <input type="file" hidden accept="image/*,.pdf"
                onChange={(e) => setArchivo(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
            </Button>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
              Se enviará a sis@grupohei.com con tu nombre, la pantalla actual y la fecha.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{enviada ? 'Cerrar' : 'Cancelar'}</Button>
        {!enviada && (
          <Button variant="contained" onClick={enviar} disabled={enviando} sx={{ bgcolor: '#4f46e5' }}>
            {enviando ? 'Enviando...' : 'Enviar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
